import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/entreprise/Chauffeur-pour-entreprise.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const valbonneEn: ChauffeurPage = {
  slug: "valbonne",
  title: "VTC & private chauffeur in Valbonne",
  subtitle:
    "Private chauffeur in Valbonne: Nice airport transfers, Sophia Antipolis, hourly hire and the Opio golf course.",
  keywords: [
    "vtc valbonne",
    "private chauffeur valbonne",
    "sophia antipolis chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Valbonne",
    subtitle:
      "Nice airport transfers, Sophia Antipolis, hourly hire and the Opio golf course — available 24/7.",
    keywords: [
      "VTC Valbonne",
      "Sophia Antipolis chauffeur",
      "Nice airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Valbonne and Sophia Antipolis",
    badges: ["Sophia Antipolis", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Valbonne",
    items: [
      {
        title: "Sophia Antipolis within reach",
        description:
          "Europe's largest technology park spreads across the plateau: headquarters, R&D centres and business parks the chauffeur reaches address by address.",
      },
      {
        title: "Provençal grid village",
        description:
          "Place des Arcades, pedestrian lanes and village restaurants: drop-off as close as possible, with no circling for a space.",
      },
      {
        title: "Nice airport in ~25 min",
        description:
          "A direct A8 link with flight tracking and a personal welcome, both ways.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Valbonne",
    services: [
      {
        title: "Nice airport transfers",
        text: "Valbonne ⇄ Nice Côte d'Azur airport in about 25 minutes via the A8, with flight tracking and a terminal welcome.",
      },
      {
        title: "Business chauffeur in Sophia Antipolis",
        text: "Executives and delegations met at the airport, multi-site meetings across the tech park, company billing.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or by the day, a dedicated chauffeur for meetings, lunches and errands.",
      },
      {
        title: "Golf & leisure",
        text: "Links to the Opio-Valbonne golf course and La Grande Bastide, with waiting on site and a return to the village.",
      },
      {
        title: "Evenings & restaurants",
        text: "Dinners around Place des Arcades and relaxed late-night returns.",
      },
    ],
  },
  locations: {
    heading: "Areas & pick-up points",
    items: [
      {
        name: "Place des Arcades",
        description: "Heart of the Provençal grid-plan village.",
      },
      {
        name: "Sophia Antipolis",
        description:
          "Headquarters, R&D centres and business parks on the plateau.",
      },
      {
        name: "Garbejaire",
        description: "Residential quarter and shops of the tech park.",
      },
      {
        name: "Opio-Valbonne golf course",
        description: "Course and clubhouse, minutes from the village.",
      },
      {
        name: "Brague valley",
        description: "Natural surroundings and hamlets of the commune.",
      },
      { name: "Nice airport", description: "~25 min via the A8." },
    ],
  },
  generic: {
    heading: "Valbonne, a Provençal village at the heart of the tech park",
    subheading:
      "Between the grid-plan bastide and Sophia Antipolis, a chauffeur who knows both faces of the town.",
    paragraphs: [
      "Valbonne has a double identity: a 16th-century village laid out on a grid around Place des Arcades, and a major share of Sophia Antipolis, Europe's largest technology park. On one side pedestrian lanes, on the other a plateau where headquarters and R&D centres spread over several kilometres.",
      "Across the tech park the addresses are scattered and poorly served without a car: a chauffeur who knows Garbejaire, the business parks and the corporate hotels saves executives and delegations real time. About 25 minutes from Nice airport via the A8, Valbonne is a frequent arrival point for business travellers.",
      "For leisure, we handle links to the Opio-Valbonne golf course, dinners around Place des Arcades and late returns, with the same car on hand from one appointment to the next on hourly hire.",
    ],
    bullets: [
      "Sophia Antipolis addresses mastered",
      "Flight tracking & company billing",
      "Opio golf & Provençal village",
    ],
  },
  process: {
    heading: "Book a chauffeur in Valbonne",
    steps: [
      "Share your route or your addresses (airport, Sophia Antipolis, golf, village).",
      "We confirm the right vehicle and the fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Direct transfer or a multi-site day, to suit your programme.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Valbonne"),
    {
      q: "How long is the drive to Nice airport from Valbonne?",
      a: "Allow about 25 minutes via the A8 outside peak hours. We track your flight to adjust the pick-up time, both ways.",
    },
    {
      q: "Do you serve businesses in Sophia Antipolis?",
      a: "Yes, we meet executives and delegations at the airport and then chain multi-site meetings across the tech park, with company billing.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during trade shows; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to the international clientele of the tech park.",
    },
    {
      q: "Which vehicles do you offer in Valbonne?",
      a: "Mercedes E-Class and S-Class sedans, a 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Do you drive to the Opio-Valbonne golf course?",
      a: "Yes, we serve the Opio-Valbonne golf course and La Grande Bastide, with waiting on site and a return to the village or hotel.",
    },
  ],
  relatedLinks: [
    {
      label: "Car service in Sophia Antipolis",
      href: "/en/chauffeur/sophia-antipolis",
    },
    { label: "Book an airport transfer", href: "/en/chauffeur/airport" },
    {
      label: "Transfers across the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Cannes chauffeur rates", href: "/en/chauffeur/cannes" },
  ],
  cta: {
    title: "Your private chauffeur in Valbonne",
    text: "Airport transfer, Sophia Antipolis or hourly hire — quick reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Valbonne 24/7 — car service & transfers",
    description:
      "Private car service in Valbonne: Nice airport transfer (~25 min), Sophia Antipolis, hourly hire and the Opio golf course. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/valbonne",
  },
};

export default valbonneEn;
